import {Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy} from '@angular/core';
import {AssessorForm} from 'src/app/_models/assessorForm';
import {ApiService} from 'src/app/_services/api.service';
import {LegendItemModel} from './legend-item.model';
import {UserService} from '../../_services/user.service';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {ViewportScroller} from '@angular/common';
import {SiteConfigService} from '../../_services/site-config.service';

@Component({
  selector: 'app-status',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class StatusComponent implements OnInit {

  protected readonly apiService = inject(ApiService);
  protected readonly userService = inject(UserService);
  protected readonly viewPortScroller = inject(ViewportScroller);
  private readonly siteConfig = inject(SiteConfigService);

  ampOffline = signal(false);
  lastUpdatedTime = signal(null);
  page = signal(1);
  pageSize = signal(10);

  private readonly cachedCollectionSize = signal(0);

  fileItems = computed(() => this.recordsQuery.data()?.records ?? []);
  collectionSize = computed(() => this.recordsQuery.data()?.totalRecords ?? this.cachedCollectionSize());

  protected recordsQuery = injectQuery(() => ({
    queryKey: ['assessor-form-status', this.page(), this.pageSize()],
    queryFn: () => this.apiService.getAssessorFormStatus(this.page(), this.pageSize()),
  }));

  constructor() {
    effect(() => {
      const total = this.recordsQuery.data()?.totalRecords;
      if (typeof total === 'number') {
        this.cachedCollectionSize.set(total);
      }
    });
  }

  protected legendItems: LegendItemModel[] = [
    new LegendItemModel('Canceled', 'The form has been canceled by user.'),
    new LegendItemModel('Completed', 'The form request has been completed. Case is closed.'),
    new LegendItemModel('Expired', 'The form was not completed within the 24-hour timeframe.'),
    new LegendItemModel('Failed', 'An error occurred during processing. Please resubmit the form.'),
    new LegendItemModel('Incomplete', 'The form request is incomplete. Please contact the Office of the Assessor for assistance.'),
    new LegendItemModel('Pending Signature', 'The form has been started, but not yet submitted.'),
    new LegendItemModel('Submitted', 'The form has been submitted and is currently being processed or has been processed.'),
  ];

  ngOnInit(): void {
    this.loadStatusChecks().then();
  }

  protected scrollToTop(): void {
    this.viewPortScroller.scrollToAnchor('statusTop');
  }

  protected onPageChange(nextPage: number): void {
    if (nextPage !== this.page()) {
      this.page.set(nextPage);
    }
  }

  protected onPageSizeChange(nextPageSize: number): void {
    if (nextPageSize !== this.pageSize()) {
      this.pageSize.set(nextPageSize);
      this.page.set(1);
    }
  }

  private async loadStatusChecks(): Promise<void> {
    try {
      if (this.siteConfig.isAmpGCMMaintenanceEnabled() || this.siteConfig.isEFileSubmissionMaintenanceEnabled()) {
        this.ampOffline.set(true);
      }
    } catch (e) {
      console.error(e);
      this.ampOffline.set(true);
    }
  }

  retryButton(): void {
    this.recordsQuery.refetch();
  }

  protected accessLink(id: string): void {
    this.apiService.accessLink(id).subscribe(data => {
      if (this.isSafari) {
        window.location.href = data.link;
      } else {
        window.open(data.link);
      }
    });
  }

  // -- Display helper --

  protected isSafari(): boolean {
    const userAgent = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(userAgent);
  }

  protected getCompleteButtonText(item: AssessorForm): string {
    switch (item.nextRequiredAction) {
      case 'partyComplete':
        return 'Complete First Signer';
      case 'complete':
        return item.numRequiredSigners > 1 ? 'Complete Second Signer' : 'Complete';
      default:
        return 'N/A';
    }
  }

  protected formatLocalDateTime(input: string | Date): string {
    let date: Date;

    if (typeof input === 'string') {
      date = new Date(input);
    } else {
      date = input;
    }

    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }

    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit'
    };

    const datePart = date.toLocaleDateString(undefined, dateOptions);
    const timePart = date.toLocaleTimeString(undefined, timeOptions);
    return `${datePart} at ${timePart}`;
  }

  // -- Debug tools --
  protected get shouldShowWUStart(): boolean {
    switch (window.location.host) {
      case 'localhost:4200':
      case 'assessorate2.lacounty.gov':
      case 'assessorate3.lacounty.gov':
        return true;
      default:
        return false;
    }
  }

  protected getWorkUnitNumber(item: AssessorForm): string {
    if (item.isLegacySubmission) {
      return 'N/A';
    }
    if (!item.generatesWorkUnitNumber) {
      return 'N/A';
    }
    return item.workUnitId <= 0 ? '' : item.workUnitNumber;
  }

  protected manuallyKickOffWorkUnit(item: AssessorForm): void {
    this.apiService.manuallyKickOffWorkUnit(item.clientId, item.nextRequiredAction).subscribe(o => {
      switch (item.nextRequiredAction) {
        case 'complete':
          item.workUnitId = -1;
          item.status = 'Submitted';
          break;
        case 'partyComplete':
          item.nextRequiredAction = 'complete';
          break;
      }
    });
  }
}

